import { API_URL as API_BASE } from '../apiConfig';
// ============================================
// src/utils/preferencesLoader.js
// ============================================
// Loads user preferences from the API and applies
// theme + accessibility CSS variables to the document.
// Used by ALL Narbodica apps.
//
// Usage:
//   import { loadAndApplyPreferences } from './utils/preferencesLoader';
//   useEffect(() => { loadAndApplyPreferences(token); }, [token]);
// ============================================

// API_BASE imported from apiConfig.js (single source of truth)

// Load OpenDyslexic font if needed
var dyslexicFontLoaded = false;
function loadDyslexicFont() {
  if (dyslexicFontLoaded) return;
  var link = document.createElement('link');
  link.href = 'https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/open-dyslexic-regular.min.css';
  link.rel = 'stylesheet';
  document.head.appendChild(link);
  dyslexicFontLoaded = true;
}

// Apply CSS variables to the document root
function applyVars(vars) {
  if (!vars) return;
  var root = document.documentElement;
  Object.keys(vars).forEach(function(key) {
    root.style.setProperty(key, vars[key]);
  });
}

// Apply accessibility overrides
function applyAccessibility(accessibility) {
  if (!accessibility) return;

  var root = document.documentElement;

  // Dyslexic font
  if (accessibility.cognitive && accessibility.cognitive.dyslexicFont) {
    loadDyslexicFont();
    root.style.setProperty('font-family', "'OpenDyslexic', 'Comic Sans MS', sans-serif");
    root.classList.add('dyslexic-font');
  }

  // Reduce animations
  if (accessibility.reduceAnimations) {
    root.classList.add('reduce-motion');
    // Inject style if not already present
    if (!document.getElementById('reduce-motion-style')) {
      var style = document.createElement('style');
      style.id = 'reduce-motion-style';
      style.textContent = '.reduce-motion *, .reduce-motion *::before, .reduce-motion *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }';
      document.head.appendChild(style);
    }
  }

  // High contrast
  if (accessibility.vision && accessibility.vision.highContrast) {
    root.classList.add('high-contrast');
  }

  // Custom font size
  if (accessibility.vision && accessibility.vision.customFontSize) {
    root.style.setProperty('font-size', accessibility.vision.customFontSize + 'px');
  }

  // Larger tap targets
  if (accessibility.motor && accessibility.motor.largerTapTargets) {
    var size = (accessibility.motor.minTapTargetSize || 44) + 'px';
    root.style.setProperty('--min-tap-target', size);
    // Inject style for larger buttons
    if (!document.getElementById('large-targets-style')) {
      var targetStyle = document.createElement('style');
      targetStyle.id = 'large-targets-style';
      targetStyle.textContent = 'button, a, [role="button"], input, select, textarea { min-height: var(--min-tap-target, 44px); min-width: var(--min-tap-target, 44px); }';
      document.head.appendChild(targetStyle);
    }
  }

  // Flash notifications
  if (accessibility.hearing && accessibility.hearing.flashNotifications) {
    if (!document.getElementById('flash-notification-style')) {
      var flashStyle = document.createElement('style');
      flashStyle.id = 'flash-notification-style';
      flashStyle.textContent = '@keyframes flash-notify { 0% { opacity: 1; } 25% { opacity: 0.3; } 50% { opacity: 1; } 75% { opacity: 0.3; } 100% { opacity: 1; } } .flash-notify { animation: flash-notify 0.6s ease-in-out; }';
      document.head.appendChild(flashStyle);
    }
    window.__flashOnNotification = true;
  }

  // Compact / reduced density
  if (accessibility.cognitive && accessibility.cognitive.reducedDensity) {
    root.classList.add('reduced-density');
    if (!document.getElementById('reduced-density-style')) {
      var densityStyle = document.createElement('style');
      densityStyle.id = 'reduced-density-style';
      densityStyle.textContent = '.reduced-density { line-height: 2 !important; } .reduced-density > * { margin-bottom: 16px; }';
      document.head.appendChild(densityStyle);
    }
  }
}

// Main loader — call after authentication
export async function loadAndApplyPreferences(token) {
  if (!token) return null;

  try {
    var res = await fetch(API_BASE + '/preferences', {
      headers: { 'Authorization': 'Bearer ' + token },
    });
    var data = await res.json();
    if (!data.success || !data.data) return null;

    var prefs = data.data;

    // Get theme CSS vars
    var themeRes = await fetch(API_BASE + '/preferences/theme/css', {
      headers: { 'Authorization': 'Bearer ' + token },
    });
    var themeData = await themeRes.json();
    if (themeData.success && themeData.data && themeData.data.vars) {
      applyVars(themeData.data.vars);
    }

    // Apply accessibility
    if (prefs.accessibility) {
      applyAccessibility(prefs.accessibility);
    }

    return prefs;
  } catch (e) {
    console.error('[Preferences] Load error:', e.message);
    return null;
  }
}

// Flash the screen (for deaf users on notification)
export function flashScreen() {
  if (!window.__flashOnNotification) return;
  document.body.classList.add('flash-notify');
  setTimeout(function() { document.body.classList.remove('flash-notify'); }, 700);
}

export default { loadAndApplyPreferences, flashScreen };
